import React, { useEffect, useState } from 'react';
import { Coach, CoachFiles, CoachView } from '../model/Coach';
import { PlanningSlot } from '../model/Planning';
import { getCoachFile, getCoachFiles, getCoachHistory, getCoachView, setCoachEnabled } from '../services/service';
import { showMessage } from '../services/popup';
import { splitHistory } from '../utils';
import { Image } from './Image';
import { PlanningSlotViewer } from './PlanningSlotViewer';

export interface CoachComponentProps {
  coach: Coach;
}

export const CoachComponent: React.FC<CoachComponentProps> = ({ coach }) => {
  const [view, setView] = useState<CoachView | undefined>(undefined);
  const [slots, setSlots] = useState<PlanningSlot[]>([]);
  const [state, setState] = useState(coach.state);
  const [files, setFiles] = useState<CoachFiles[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const get = async () => {
      setView(await getCoachView(coach.id));
      setSlots(await getCoachHistory(coach.id));
      setFiles(await getCoachFiles(coach.id));
      setLoading(false);
    };

    get();
  }, [coach.id]);

  const split = splitHistory(slots);

  const changeState = async () => {
    try {
      const r = await setCoachEnabled(coach.id, !state);
      if (r) {
        setState(!state);
      }
    } catch {
      showMessage('Une erreur est survenue.');
    }
  };

  const getFile = async (file: CoachFiles) => {
    try {
      const r = await getCoachFile(coach.id, file.name);
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(r);
      link.setAttribute('download', `${file.name}.${file.extension}`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="page-stack">
      <section className="detail-grid">
        <article className="panel">
          <Image isClient={false} id={coach.id} />
          <h3>Profil coach</h3>
          <div className="info-list">
            <div><b>Nom:</b> {coach.lastname}</div>
            <div><b>Prenom:</b> {coach.firstname}</div>
            <div><b>Note:</b> {coach.note}</div>
            <div><b>Telephone:</b> {coach.phoneNumber}</div>
            <div><b>Cours:</b> {coach.specialities.map((k) => k.name).join(', ')}</div>
            <div><b>Etat:</b> <span className={`pill ${state ? 'ok' : 'bad'}`}>{state ? 'Actif' : 'Inactif'}</span></div>
            <button className={`ghost-btn ${state ? 'danger' : ''}`} onClick={changeState}>
              {state ? 'Desactiver' : 'Activer'}
            </button>
          </div>
        </article>

        <article className="panel">
          <h3>Informations detaillees</h3>
          {loading ? (
            <div>Chargement...</div>
          ) : (
            <div className="info-list">
              <div><b>Adresse:</b> {view?.address.description}</div>
              <div><b>Seances faites:</b> {view?.sessionsDone}</div>
              <div><b>Email:</b> {view?.email}</div>
              <div><b>Lieux de travail:</b> {view?.workPlaces.map((w) => w.description).join(' - ')}</div>
            </div>
          )}

          <h4>Fichiers</h4>
          <div className="file-list">
            {files.map((f, i) => (
              <div key={i} className="row-between card-inline">
                <span>{f.name}.{f.extension}</span>
                <button className="ghost-btn" onClick={() => getFile(f)}>Telecharger</button>
              </div>
            ))}
          </div>
        </article>
      </section>

      <section className="panel">
        <h3>Prochaines seances</h3>
        <div className="slot-stack">
          {split.validated.map((s, i) => (
            <PlanningSlotViewer key={i} date={s.date} userId={s.clientId} userName={s.clientName} state={s.state} address={s.location.description} isClient={false} />
          ))}
        </div>
      </section>

      <section className="panel">
        <h3>En attente de validation</h3>
        <div className="slot-stack">
          {split.validating.map((s, i) => (
            <PlanningSlotViewer key={i} date={s.date} userId={s.clientId} userName={s.clientName} state={s.state} address={s.location.description} isClient={false} />
          ))}
        </div>
      </section>

      <section className="panel">
        <h3>Seances passees</h3>
        <div className="slot-stack">
          {split.passed.map((s, i) => (
            <PlanningSlotViewer key={i} date={s.date} userId={s.clientId} userName={s.clientName} state={s.state} address={s.location.description} isClient={false} />
          ))}
        </div>
      </section>
    </div>
  );
};
